import type {
  ISetupOrdersChoice,
  IStoreSetupStatus,
} from '@/interfaces/IStoreSetup';
import type { IMenuDraft, IMenuImportSnapshot } from '@/interfaces/IMenuImport';
import { listJoin } from './display-names';
import { mercadoPagoAvailable } from './store-country';

// ---------------------------------------------------------------------------
// Translatable texts
// ---------------------------------------------------------------------------

/** An i18n key with its params; `count` set → render with `$tc(key, count, params)`. */
export interface ITextRef {
  key: string;
  params: Record<string, string | number>;
  count?: number;
}

/** Parts joined with `listJoin` and upper-cased first letter («Mesas, retiro y delivery hasta 5 km»). */
export interface ITextList {
  parts: ITextRef[];
}

export type TTextRef = ITextRef | ITextList;

/** `(key, params, count?) => text`; components map it to `$t` / `$tc`. */
export type TTextTranslate = (
  key: string,
  params: Record<string, string | number>,
  count?: number
) => string;

function textRef(key: string, params: ITextRef['params'] = {}): ITextRef {
  return { key, params };
}

function capitalize(text: string, locale: string): string {
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

/** The one place a `TTextRef` becomes text. */
export function renderTextRef(
  ref: TTextRef,
  translate: TTextTranslate,
  locale: string
): string {
  if ('parts' in ref)
    return capitalize(
      listJoin(
        ref.parts.map((part) => translate(part.key, part.params, part.count)),
        locale
      ),
      locale
    );
  return translate(ref.key, ref.params, ref.count);
}

// ---------------------------------------------------------------------------
// Which screen `/:id/setup` opens (§4.12)
// ---------------------------------------------------------------------------

export type TSetupScreen = 'source' | 'workspace' | 'orders' | 'live';

export type TSetupScreenResolution =
  | { screen: TSetupScreen; checklistMode: boolean }
  | { redirect: 'dashboard' };

function draftHasCategories(draft: IMenuDraft | null): boolean {
  return (draft?.categoryOrder.length ?? 0) > 0;
}

function draftIsEmpty(draft: IMenuDraft | null): boolean {
  return Object.keys(draft?.items ?? {}).length === 0;
}

/** The menu step: the workspace once there is something to show (a read, categories, or a failed read to retry). */
function menuScreen(snapshot: IMenuImportSnapshot | null): TSetupScreen {
  if (!snapshot) return 'source';
  const readRunning =
    snapshot.status === 'reading' || snapshot.read?.status === 'running';
  const failedEmptyRead =
    snapshot.read?.status === 'failed' && draftIsEmpty(snapshot.draft);
  return readRunning || draftHasCategories(snapshot.draft) || failedEmptyRead
    ? 'workspace'
    : 'source';
}

/**
 * No setup row → dashboard; `?step=orders` while orders are not configured →
 * Pedidos in checklist mode (any stage); stage `menu` → workspace or source;
 * `orders` → Pedidos, or Live once orders are configured (from the checklist
 * before the first publish, or by another device); `live` → Live; `done` →
 * dashboard.
 */
export function resolveSetupScreen(
  status: IStoreSetupStatus,
  snapshot: IMenuImportSnapshot | null,
  query: Readonly<Record<string, unknown>>
): TSetupScreenResolution {
  const setup = status.setup;
  if (!setup) return { redirect: 'dashboard' };
  if (query.step === 'orders' && !setup.ordersConfiguredAt)
    return { screen: 'orders', checklistMode: true };
  switch (setup.stage) {
    case 'menu':
      return { screen: menuScreen(snapshot), checklistMode: false };
    case 'orders':
      // Configured already: the Live screen moves the stage on (its mount PATCH).
      return {
        screen: setup.ordersConfiguredAt ? 'live' : 'orders',
        checklistMode: false,
      };
    case 'live':
      return { screen: 'live', checklistMode: false };
    default:
      return { redirect: 'dashboard' };
  }
}

// ---------------------------------------------------------------------------
// «Pedidos» summary (§13.4)
// ---------------------------------------------------------------------------

/**
 * «Mesas, retiro y delivery hasta 5 km»; «Solo menú, sin pedidos» for
 * menu-only; «Hoy: mesas y retiro. Delivery apagado.» when orders were never
 * configured (null choice).
 */
export function ordersSummary(choice: ISetupOrdersChoice | null): TTextRef {
  if (!choice) return textRef('storeSetup.orders.summary.skipped');
  if (choice.menuOnly) return textRef('storeSetup.orders.summary.menuOnly');
  const parts: ITextRef[] = [];
  if (choice.tableOrders)
    parts.push(textRef('storeSetup.orders.summary.table'));
  if (choice.takeAway) parts.push(textRef('storeSetup.orders.summary.pickup'));
  if (choice.delivery) {
    const km = choice.deliveryRadiusKm;
    if (km === null)
      parts.push(textRef('storeSetup.orders.summary.deliveryPlain'));
    else if (km === 0)
      parts.push(textRef('storeSetup.orders.summary.deliveryNoLimit'));
    else parts.push(textRef('storeSetup.orders.summary.delivery', { km }));
  }
  return { parts };
}

// ---------------------------------------------------------------------------
// Dashboard checklist (§4.10)
// ---------------------------------------------------------------------------

export type TChecklistKey = 'menu' | 'orders' | 'mercadoPago' | 'qr' | 'logo';
export type TChecklistBand = 'orders' | 'more';

export interface IChecklistAction {
  labelKey: string;
  /** Router path. */
  to: string;
}

export interface IChecklistSecondaryAction {
  labelKey: string;
  action: 'qrDone';
}

export interface IChecklistRow {
  key: TChecklistKey;
  band: TChecklistBand;
  done: boolean;
  titleKey: string;
  status: TTextRef;
  /** Shown only while the row is pending. */
  action: IChecklistAction | null;
  secondary?: IChecklistSecondaryAction;
}

/** The store fields the checklist reads (an `IRestaurant` fits). */
export interface IChecklistStore {
  stringId: string;
  country: string;
  logo?: string | null;
}

/** Where «Cargar su menú» leads: the setup flow while it is on the menu step, else the import page. */
export function menuTarget(
  status: IStoreSetupStatus,
  stringId: string
): string {
  return status.setup?.stage === 'menu'
    ? `/${stringId}/setup`
    : `/${stringId}/menu/import`;
}

function menuRow(
  status: IStoreSetupStatus,
  store: IChecklistStore,
  multipleMenus: boolean
): IChecklistRow {
  const { visibleItems, activeSession } = status.menu;
  const row: IChecklistRow = {
    key: 'menu',
    band: 'orders',
    done: visibleItems > 0,
    titleKey: 'storeSetup.checklist.menu',
    status: {
      key: 'storeSetup.checklist.menuDone',
      params: { count: visibleItems },
      count: visibleItems,
    },
    action: null,
  };
  if (row.done) return row;
  const hasDraft = (activeSession?.items ?? 0) > 0;
  row.status = textRef(
    hasDraft
      ? 'storeSetup.checklist.menuDraft'
      : 'storeSetup.checklist.menuEmpty'
  );
  // Stores with several menus cannot use the AI import (§4.11): no action.
  if (!multipleMenus)
    row.action = {
      labelKey: hasDraft
        ? 'storeSetup.checklist.menuReview'
        : 'storeSetup.checklist.menuAction',
      to: menuTarget(status, store.stringId),
    };
  return row;
}

function ordersRow(
  status: IStoreSetupStatus,
  store: IChecklistStore
): IChecklistRow {
  const done = !!status.setup?.ordersConfiguredAt;
  return {
    key: 'orders',
    band: 'orders',
    done,
    titleKey: 'storeSetup.checklist.orders',
    status: ordersSummary(done ? status.setup?.ordersChoice ?? null : null),
    action: done
      ? null
      : {
          labelKey: 'storeSetup.checklist.ordersAction',
          to: `/${store.stringId}/setup?step=orders`,
        },
  };
}

function mercadoPagoRow(
  status: IStoreSetupStatus,
  store: IChecklistStore
): IChecklistRow {
  const done = !!status.ordering?.mercadoPagoConnected;
  return {
    key: 'mercadoPago',
    band: 'orders',
    done,
    titleKey: 'storeSetup.checklist.mercadoPago',
    status: textRef(
      done
        ? 'storeSetup.checklist.mercadoPagoDone'
        : 'storeSetup.checklist.mercadoPagoText'
    ),
    action: done
      ? null
      : {
          labelKey: 'storeSetup.checklist.mercadoPagoAction',
          to: `/${store.stringId}/settings?tab=orders`,
        },
  };
}

function qrRow(
  status: IStoreSetupStatus,
  store: IChecklistStore
): IChecklistRow {
  const done = !!status.setup?.qrDoneAt;
  const row: IChecklistRow = {
    key: 'qr',
    band: 'orders',
    done,
    titleKey: 'storeSetup.checklist.qr',
    status: textRef(
      done ? 'storeSetup.checklist.qrDone' : 'storeSetup.checklist.qrText'
    ),
    action: done
      ? null
      : {
          labelKey: 'storeSetup.checklist.qrAction',
          to: `/${store.stringId}/qr`,
        },
  };
  if (!done)
    row.secondary = {
      labelKey: 'storeSetup.checklist.qrDoneManually',
      action: 'qrDone',
    };
  return row;
}

function logoRow(store: IChecklistStore): IChecklistRow {
  const done = !!store.logo;
  return {
    key: 'logo',
    band: 'more',
    done,
    titleKey: 'storeSetup.checklist.logo',
    status: textRef(
      done ? 'storeSetup.checklist.logoDone' : 'storeSetup.checklist.logoText'
    ),
    action: done
      ? null
      : {
          labelKey: 'storeSetup.checklist.logoAction',
          to: `/${store.stringId}/settings?tab=basic`,
        },
  };
}

/**
 * The five rows of «Ponga su tienda a punto», in display order; the Mercado
 * Pago row only where Mercado Pago can be connected. `multipleMenus`
 * (restaurant settings) hides the menu row's action (§4.11).
 */
export function buildChecklist(
  status: IStoreSetupStatus,
  store: IChecklistStore,
  multipleMenus = false
): IChecklistRow[] {
  const rows = [
    menuRow(status, store, multipleMenus),
    ordersRow(status, store),
  ];
  if (mercadoPagoAvailable(store.country))
    rows.push(mercadoPagoRow(status, store));
  rows.push(qrRow(status, store), logoRow(store));
  return rows;
}
